import * as React from 'react';
import { Animated, Image, StyleSheet, Text, TouchableOpacity, TouchableWithoutFeedback, View } from 'react-native';

import color from '../../../style/color';
import { font } from '../../../style/text';
import { getLocalizedTexts } from '../../../Locales';
import { getRegion, localizeAsset, LocalizedAsset, Region } from '../../../lib/auth/region';
import { logEventWithoutAuthentication } from '../../../api/rest/events';
import { InstallationStep } from '../../../lib/mobileSafariExtension/mobileSafariExtension';
import { NewDesignSystemButton } from './Buttons';
import { containerStyleWithGap, newDesignSystemFontStyles, normalizeHeight, normalizeWidth } from './Styles';

const jokoLogoTileIcon = { uri: '/assets/images/logos/lightning-rounded.svg' };
const lockIcon = { uri: '/assets/images/icons/lock-black.svg' };

// The merchant offer cards (with the bottom fade) are exported as a single illustration from Figma, with one variant per region.
const offersIllustration: LocalizedAsset = {
    [Region.FR]: { uri: '/assets/images/illustrations/mobile-safari-extension-installation/intent-validation-offers.png' },
    [Region.US]: {
        uri: '/assets/images/illustrations/mobile-safari-extension-installation/intent-validation-offers-us.png',
    },
    [Region.GB]: {
        uri: '/assets/images/illustrations/mobile-safari-extension-installation/intent-validation-offers-us.png',
    },
};
// Pixel dimensions of the exported offers illustration, so the image container matches its aspect ratio exactly.
// The export is 373px wide at 1x: 326px of cards plus the drop-shadow bleed, so rendering it at a 373px base
// width makes the cards match the 326px width they have in the 375px-wide Figma frame.
const OFFERS_ILLUSTRATION_ASPECT_RATIO = 1119 / 800;
const OFFERS_ILLUSTRATION_BASE_WIDTH = 373;

export function IntentValidationScreen({
    variant,
    isOnboarding,
    isBeforeOnboarding,
    isDevStackMode,
    userId,
    setStep,
}: {
    variant: 'B' | 'C';
    isOnboarding: boolean;
    isBeforeOnboarding: boolean;
    isDevStackMode: boolean;
    userId: string | undefined;
    setStep: (step: InstallationStep) => void;
}) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.intentValidation;
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    React.useEffect(() => {
        logEventWithoutAuthentication(
            userId,
            {
                type: 'viewedIntentValidationScreenMobileSafariExtensionInstallationFlow',
                payload: { abTestGroup: variant, isOnboarding, isBeforeOnboarding, isRevampedOnboardingFlow: true },
            },
            isDevStackMode
        );
        // eslint-disable-next-line
    }, []); // This effect should run only once, when the screen is first displayed
    const onPressContinue = (from: 'screen' | 'drawer') => () => {
        setStep(InstallationStep.activateStep);
        logEventWithoutAuthentication(
            userId,
            {
                type:
                    from === 'screen'
                        ? 'clickedContinueIntentValidationScreenMobileSafariExtensionInstallationFlow'
                        : 'clickedContinueHowItWorksDrawerMobileSafariExtensionInstallationFlow',
                payload: { abTestGroup: variant, isOnboarding, isBeforeOnboarding, isRevampedOnboardingFlow: true },
            },
            isDevStackMode
        );
    };
    const onPressHowItWorks = () => {
        setIsDrawerOpen(true);
        logEventWithoutAuthentication(
            userId,
            {
                type: 'clickedHowItWorksIntentValidationScreenMobileSafariExtensionInstallationFlow',
                payload: { abTestGroup: variant, isRevampedOnboardingFlow: true },
            },
            isDevStackMode
        );
    };
    const onDismissDrawer = () => {
        setIsDrawerOpen(false);
        logEventWithoutAuthentication(
            userId,
            {
                type: 'clickedGotItHowItWorksDrawerMobileSafariExtensionInstallationFlow',
                payload: { abTestGroup: variant, isRevampedOnboardingFlow: true },
            },
            isDevStackMode
        );
    };
    return (
        <View style={containerStyleWithGap(24)}>
            <View style={styles.containerHeader}>
                <Image source={jokoLogoTileIcon} style={styles.jokoLogoTile} />
                <View style={styles.containerTitle}>
                    <Text style={[newDesignSystemFontStyles.titleXL, styles.textCentered]}>
                        {variant === 'B' ? texts.titleB : texts.titleC}
                    </Text>
                    {variant === 'C' ? (
                        <Text style={[newDesignSystemFontStyles.paragraphMedium, styles.textSubtitle]}>
                            {texts.subtitleC}
                        </Text>
                    ) : null}
                </View>
                <TouchableOpacity onPress={onPressHowItWorks} activeOpacity={0.7}>
                    <Text style={[newDesignSystemFontStyles.actionMediumUnderline, styles.howItWorksLink]}>
                        {texts.howItWorksLink}
                    </Text>
                </TouchableOpacity>
            </View>
            <View style={styles.containerIllustration}>
                <Image source={localizeAsset(offersIllustration)} style={styles.imageOffersIllustration} />
            </View>
            <View style={styles.containerContinueButton}>
                <NewDesignSystemButton
                    onPress={onPressContinue('screen')}
                    textStyle={newDesignSystemFontStyles.actionMedium}>
                    {texts.continueButton}
                </NewDesignSystemButton>
            </View>
            {isDrawerOpen ? (
                <HowItWorksDrawer
                    onContinue={onPressContinue('drawer')}
                    onDismiss={onDismissDrawer}
                    region={getRegion()}
                />
            ) : null}
        </View>
    );
}

function HowItWorksDrawer({
    onContinue,
    onDismiss,
    region,
}: {
    onContinue: () => void;
    onDismiss: () => void;
    region: Region;
}) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.intentValidation.drawer;
    const appearanceAnimation = React.useRef(new Animated.Value(0)).current;
    React.useEffect(() => {
        Animated.timing(appearanceAnimation, { toValue: 1, duration: 250, useNativeDriver: true }).start();
        // eslint-disable-next-line
    }, []);
    React.useEffect(() => {
        // A web page cannot paint over Safari's own chrome (status bar / address bar), but Safari tints it from
        // the theme-color meta tag: dim it to match the backdrop (white under 40% black) while the drawer is open.
        const themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
        const initialThemeColor = themeColorMeta?.content;
        if (themeColorMeta) themeColorMeta.content = '#999999';
        return () => {
            if (themeColorMeta && initialThemeColor) themeColorMeta.content = initialThemeColor;
        };
    }, []);
    return (
        <View style={StyleSheet.absoluteFill}>
            <TouchableWithoutFeedback onPress={onDismiss}>
                <Animated.View style={[styles.drawerBackdrop, { opacity: appearanceAnimation }]} />
            </TouchableWithoutFeedback>
            <Animated.View
                style={[
                    styles.drawerSheet,
                    {
                        transform: [
                            {
                                translateY: appearanceAnimation.interpolate({
                                    inputRange: [0, 1],
                                    outputRange: [normalizeHeight(200), 0],
                                }),
                            },
                        ],
                        opacity: appearanceAnimation,
                    },
                ]}>
                <View style={styles.drawerScrollIndicator} />
                <Text style={[newDesignSystemFontStyles.titleXL, styles.textCentered]}>{texts.title}</Text>
                <View style={styles.drawerStepsCard}>
                    <DrawerStepRow itemNumber={1} title={texts.step1Title} description={texts.step1Description} />
                    <View style={styles.drawerStepDivider} />
                    <DrawerStepRow itemNumber={2} title={texts.step2Title} description={texts.step2Description} />
                    <View style={styles.drawerStepDivider} />
                    <DrawerStepRow itemNumber={3} title={texts.step3Title} description={texts.step3Description} />
                </View>
                <View style={styles.drawerPrivacyRow}>
                    <Image source={lockIcon} style={styles.imageLockIcon} />
                    <Text style={styles.textPrivacyNote}>{texts.privacyNote}</Text>
                </View>
                <View style={styles.containerContinueButton}>
                    <NewDesignSystemButton onPress={onContinue} textStyle={newDesignSystemFontStyles.actionMedium}>
                        {texts.continueButton}
                    </NewDesignSystemButton>
                </View>
                <TouchableOpacity onPress={onDismiss} activeOpacity={0.7}>
                    <Text style={styles.textLink}>{texts.gotIt}</Text>
                </TouchableOpacity>
            </Animated.View>
        </View>
    );
}

function DrawerStepRow({ itemNumber, title, description }: { itemNumber: number; title: string; description: string }) {
    return (
        <View style={styles.drawerStepRow}>
            <View style={styles.drawerStepNumberTile}>
                <Text style={styles.textStepNumber}>{itemNumber}</Text>
            </View>
            <View style={styles.drawerStepTexts}>
                <Text style={styles.textStepTitle}>{title}</Text>
                <Text style={[newDesignSystemFontStyles.newCopyInstructionText, styles.textStepDescription]}>
                    {description}
                </Text>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    containerHeader: {
        alignItems: 'center',
        gap: 24,
        alignSelf: 'stretch',
    },
    jokoLogoTile: {
        width: 40,
        height: 40,
        borderRadius: 8,
    },
    containerTitle: {
        alignItems: 'center',
        gap: 8,
        alignSelf: 'stretch',
    },
    textCentered: {
        textAlign: 'center',
        color: color.black,
    },
    // Paragraph/Medium (Ambit) in Content-Core-Secondary, same as the authorization step subtitle.
    textSubtitle: {
        color: color.boulder,
        textAlign: 'center',
    },
    // Ambit (Action/Medium underline), so the "How it works" link matches the Ambit title & subtitle above it.
    howItWorksLink: {
        color: color.black,
        textAlign: 'center',
    },
    // The drawer text button uses Instrument Sans (Body/M-medium); no Instrument Sans 16px token exists yet in newDesignSystemFontStyles.
    textLink: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 16,
        lineHeight: 16 * 1.2,
        color: color.black,
        textAlign: 'center',
        textDecorationLine: 'underline',
    },
    containerIllustration: {
        flex: 1,
        alignSelf: 'stretch',
        alignItems: 'center',
    },
    imageOffersIllustration: {
        width: normalizeWidth(OFFERS_ILLUSTRATION_BASE_WIDTH, 1, 430),
        height: normalizeWidth(OFFERS_ILLUSTRATION_BASE_WIDTH, 1, 430) / OFFERS_ILLUSTRATION_ASPECT_RATIO,
        resizeMode: 'contain',
    },
    containerContinueButton: {
        alignSelf: 'stretch',
    },
    drawerBackdrop: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
    drawerSheet: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        gap: 16,
        paddingHorizontal: 16,
        paddingTop: 8,
        paddingBottom: normalizeHeight(24),
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        backgroundColor: color.white,
    },
    drawerScrollIndicator: {
        width: 60,
        height: 4,
        borderRadius: 10,
        backgroundColor: color.alto,
    },
    drawerStepsCard: {
        alignSelf: 'stretch',
        gap: 16,
        padding: 16,
        borderWidth: 1,
        borderColor: color.mercury,
        borderRadius: 16,
        backgroundColor: color.white,
    },
    drawerStepRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    drawerStepDivider: {
        alignSelf: 'stretch',
        height: 1,
        backgroundColor: color.mercury,
    },
    drawerStepNumberTile: {
        width: 48,
        height: 48,
        justifyContent: 'center',
        alignItems: 'center',
    },
    textStepNumber: {
        fontFamily: font.ambitBlack,
        fontSize: 22,
        lineHeight: 22 * 1.2,
        color: color.black,
    },
    drawerStepTexts: {
        flex: 1,
        gap: 4,
    },
    textStepTitle: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 16,
        lineHeight: 16 * 1.2,
        color: color.black,
    },
    textStepDescription: {
        color: color.boulder,
    },
    drawerPrivacyRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        alignSelf: 'stretch',
    },
    imageLockIcon: {
        width: 16,
        height: 16,
    },
    textPrivacyNote: {
        fontFamily: font.instrumentSansMedium,
        fontSize: 14,
        lineHeight: 14 * 1.4,
        color: color.black,
    },
});
